import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  TemplateRef,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, filter, finalize, take, tap } from 'rxjs';
import { v4 as uuid } from 'uuid';
import cloneDeep from 'lodash.clonedeep';

import {
  ConfirmDialogComponent,
  TimeDisplayComponent,
  TimeEditComponent,
} from '@shared/components';
import { Rights, StaticMessage } from '@shared/enums';
import { DprActivityData, WarningStatusType } from '@dpr-details/enums';
import {
  DprActivityCopyOption,
  DprDetailCopyOption,
} from '@dpr-details/models';
import {
  DprActivityCustomDataGridData,
  DprActivityGridData,
  DprDetailsGridBaseComponent,
} from '@dpr-details/models/grid';
import {
  DateDisplayComponent,
  DateEditComponent,
  MandatoryDateComponent,
  MandatoryTextComponent,
  NumericEditComponent,
  SelectDisplayComponent,
  SelectEditComponent,
  TextDisplayComponent,
  TextEditComponent,
} from '@grid/components';
import {
  SortDescriptor,
  GridApi,
  GridSync,
  SelectionItem,
  GridColumn,
  GridOptions,
  HeaderOptions,
} from '@grid/models';
import {
  buildBooleanValues,
  compareTime,
  Constants,
  convertTimeToDate,
  getCurrentTimeAsString,
  getStaticMessage,
  hasValue,
} from '@shared/utils';
import {
  Activity,
  ActivityGroup,
  ActivityMobilisation,
  ActivityStream,
  CustomData,
  Location,
  SubprojectConfiguration,
} from '@shared/models/project-setup';
import { DprEditActivityDialogEditComponent } from '../dpr-edit-activity-dialog-edit/dpr-edit-activity-dialog-edit.component';
import {
  DprActivityStatusService,
  DprActivityCopyService,
  DprActivityCreateService,
} from '@dpr-details/services';
import { DprActivitiesGridService } from '@dpr-details/services/grid';
import { DprDetailsCopyActionModalComponent } from '../dpr-details-copy-action-modal/dpr-details-copy-action-modal.component';
import { GridSortDirection, GridSortMode, GridAlignment } from '@grid/enums';
import { DataType, DateFilter, UserPreferences } from '@shared/models';
import { MergeService, ToastNotificationService } from '@shared/services';
import { selectTooltipMessage } from '@grid/utils/select';
import { GridColumns, GridDefaultLabels } from '@shared/utils/grid';
import { Constants as GridConstants } from '@grid/utils/constants';
import { DprActivitiesDialogModeComponent } from '../dpr-activities-dialog-mode/dpr-activities-dialog-mode.component';
import { DprCopyActivityDialogEditComponent } from '../dpr-copy-activity-dialog-edit/dpr-copy-activity-dialog-edit.component';
import { DprImportActivitiesDialogComponent } from '../dpr-import-activities-dialog/dpr-import-activities-dialog.component';
import { SyncService } from '@shared/services/grid';
import {
  CalculatedCustomDataPipe,
  CustomDataFormulaPipe,
  ToUtcDateTimePipe,
} from '@shared/pipes';

@UntilDestroy()
@Component({
  selector: 'app-dpr-activities-grid',
  templateUrl: './dpr-activities-grid.component.html',
  styleUrls: ['./dpr-activities-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprActivitiesGridComponent
  extends DprDetailsGridBaseComponent<DprActivityGridData>
  implements OnChanges
{
  //#region Inputs

  @Input({ required: true }) activityStream: ActivityStream;

  @Input() isAppOnline: boolean;

  @Input() availableDates: DateFilter;

  @Input() userPreferences: UserPreferences;
  @Input() isHandset: boolean;

  //#endregion

  @Output() copyActivities = new EventEmitter<DprActivityCopyOption>();

  @Output() reactivateParentContainer = new EventEmitter<void>();

  //#region Templates

  @ViewChild('statusTemplate', { static: true })
  statusTemplate: TemplateRef<unknown>;

  @ViewChild('actionsTemplate', { static: true })
  actionsTemplate: TemplateRef<unknown>;

  @ViewChild('durationTemplate', { static: true })
  durationTemplate: TemplateRef<unknown>;

  @ViewChild('calculatedCustomDataTemplate', { static: true })
  calculatedCustomDataTemplate: TemplateRef<unknown>;

  @ViewChild('customDataFormulaTemplate', { static: true })
  customDataFormulaTemplate: TemplateRef<unknown>;

  @ViewChild('infoTemplate', { static: true })
  infoTemplate: TemplateRef<unknown>;

  @ViewChild('sortTemplate', { static: true })
  sortTemplate: TemplateRef<unknown>;

  @ViewChild('copyActivitiesTemplate', { static: true })
  copyActivitiesTemplate: TemplateRef<unknown>;

  @ViewChild('customDataNotApplicableTemplate', { static: true })
  customDataNotApplicableTemplate: TemplateRef<unknown>;

  @ViewChild('timeComponent', { static: true })
  timeComponent: TemplateRef<any>;

  @ViewChild('addDialogButtonTemplate', { static: true })
  addDialogButtonTemplate: TemplateRef<any>;

  @ViewChild('importActivitiesTemplate', { static: true })
  importActivitiesTemplate: TemplateRef<unknown>;

  //#endregion

  DprActivityData = DprActivityData;

  gridEntityName = getStaticMessage(StaticMessage.ActivityColumn);
  activityGroupFieldName = getStaticMessage(StaticMessage.ActivityGroupColumn);
  locationFieldName = getStaticMessage(StaticMessage.LocationColumn);
  startFieldName = getStaticMessage(StaticMessage.StartColumn);
  finishFieldName = getStaticMessage(StaticMessage.FinishColumn);
  addShortcutTooltip = getStaticMessage(StaticMessage.AddShortcutTooltip);

  Rights = Rights;

  ascendingDefinition: SortDescriptor<DprActivityGridData>[] = [
    { columnDef: 'start', direction: GridSortDirection.Ascending },
    { columnDef: 'finish', direction: GridSortDirection.Ascending },
  ];
  descendingDefinition: SortDescriptor<DprActivityGridData>[] = [
    { columnDef: 'start', direction: GridSortDirection.Descending },
    { columnDef: 'finish', direction: GridSortDirection.Descending },
  ];
  sortOptions = [
    {
      name: $localize`:@@dprDetails.dprActivitiesGrid.lblOldestToNewest:Sort by Oldest to Newest`,
      icon: 'sort-descending',
      definition: this.ascendingDefinition,
    },
    {
      name: $localize`:@@dprDetails.dprActivitiesGrid.lblNewestToOldest:Sort by Newest to Oldest`,
      icon: 'sort-ascending',
      definition: this.descendingDefinition,
    },
  ];
  selectedSortOption = this.sortOptions[0];

  activityMobilisation: ActivityMobilisation[];

  private copyActivityDialogRef?: MatDialogRef<DprCopyActivityDialogEditComponent>;
  private importActivitiesDialogRef?: MatDialogRef<DprImportActivitiesDialogComponent>;
  private dialogModeRef?: MatDialogRef<DprActivitiesDialogModeComponent>;

  constructor(
    private dprActivityCreateService: DprActivityCreateService,
    private dprActivityStatusService: DprActivityStatusService,
    private dprActivitiesGridService: DprActivitiesGridService,
    private dprActivityCopyService: DprActivityCopyService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private mergeService: MergeService,
    private toastNotificationService: ToastNotificationService,
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    private syncService: SyncService,
    private calculatedCustomDataPipe: CalculatedCustomDataPipe,
    private customDataFormulaPipe: CustomDataFormulaPipe,
    private toUtcDateTimePipe: ToUtcDateTimePipe
  ) {
    super();
  }

  //#region Getters

  get isDialogModeEditOpen(): boolean {
    return !!this.dialogModeRef?.componentInstance;
  }

  get isImportActivitiesDialogOpen(): boolean {
    return !!this.importActivitiesDialogRef?.componentInstance;
  }

  get activities(): Activity[] {
    return this.projectSetup.activityList;
  }

  get activityGroups(): ActivityGroup[] {
    return this.projectSetup.activityGroupList;
  }

  get locations(): Location[] {
    return this.projectSetup.locationList;
  }

  get subprojectConfiguration(): SubprojectConfiguration {
    return this.projectSetup.subprojectConfiguration;
  }

  get inlineCustomData(): CustomData[] {
    return this.projectSetup.activityStreamInlineCustomDataList
      .filter((x) => x.activityStreamId === this.activityStream.id)
      .map((x) =>
        this.projectSetup.customDataList.find((y) => y.id === x.customDataId)
      )
      .sort((a, b) => a.priority - b.priority);
  }

  get inlineCustomDataColumns(): GridColumn<DprActivityGridData>[] {
    const columns: GridColumn<DprActivityGridData>[] = [];

    if (this.subprojectConfiguration.enableCalculatedCustomData) {
      columns.push({
        columnDef: 'calculatedCustomData',
        header: {
          name: this.subprojectConfiguration.calculatedCustomDataConfiguration
            .name,
          alignment: GridAlignment.Right,
        },
        tooltip: {
          message: (context) => this.getCalculatedCustomDataValue(context.data),
          disabled: this.isHandset,
        },
        columnAlignment: GridAlignment.Right,
        allowEditing: false,
        display: { type: this.calculatedCustomDataTemplate },
        tabIndex: -1,
        disableFocusedBackground: true,
        width: 100,
        allowSorting: false,
      });
    }

    if (this.subprojectConfiguration.enableCustomDataFormula) {
      columns.push({
        columnDef: 'customDataFormula',
        header: {
          name: this.subprojectConfiguration.customDataFormulaConfiguration
            .name,
          alignment: GridAlignment.Right,
        },
        tooltip: {
          message: (context) => this.getCustomDataFormulaValue(context.data),
          disabled: this.isHandset,
        },
        columnAlignment: GridAlignment.Right,
        allowEditing: false,
        display: { type: this.customDataFormulaTemplate },
        tabIndex: -1,
        disableFocusedBackground: true,
        width: 100,
        allowSorting: false,
      });
    }

    this.inlineCustomData.forEach((x) => {
      const header: HeaderOptions<DprActivityGridData> = {
        name: x.name,
      };

      let gridColumn: GridColumn<DprActivityGridData> = {
        columnDef: `${x.id}`,
        header: header,
        minWidth: 100,
        allowSorting: false,
        onCellEdited: (data: DprActivityGridData) => {
          this.inlineCustomDataChanged(data, x.id);
          this.computeStatusForAllRows();
        },
        tabIndex: (data) => (this.canEditInlineCustomData(data, x.id) ? 0 : -1),
        disableFocusedBackground: (data) =>
          !this.canEditInlineCustomData(data, x.id),
        tooltip: {
          disabled: this.isHandset,
        },
      };

      switch (x.dataTypeId) {
        case DataType.FreeText: {
          gridColumn = {
            ...gridColumn,
            display: (data) => {
              return this.canEditInlineCustomData(data, x.id)
                ? {
                    type: this.markAsMandatory(data, x.id)
                      ? MandatoryTextComponent
                      : TextDisplayComponent,
                  }
                : { type: this.customDataNotApplicableTemplate };
            },
            edit: {
              type: TextEditComponent,
              config: {
                options: {
                  maxLength: Constants.FieldLimits.maxFreeTextDataTypeLimit,
                },
              },
              allowEditing: (data) => this.canEditInlineCustomData(data, x.id),
            },
          };
          break;
        }
        case DataType.Number: {
          gridColumn = {
            ...gridColumn,
            header: {
              ...header,
              alignment: GridAlignment.Right,
            },
            columnAlignment: GridAlignment.Right,
            display: (data) => {
              return this.canEditInlineCustomData(data, x.id)
                ? {
                    type: this.markAsMandatory(data, x.id)
                      ? MandatoryTextComponent
                      : TextDisplayComponent,
                  }
                : { type: this.customDataNotApplicableTemplate };
            },
            edit: {
              type: NumericEditComponent,
              config: {
                options: {
                  step: 1 / Math.pow(10, x.decimalCount ?? 2),
                  min: 0,
                  max: Constants.NumericOptions.maxActivitiesCustomData,
                  decimalPrecision: x.decimalCount ?? 2,
                },
              },
              allowEditing: (data) => this.canEditInlineCustomData(data, x.id),
            },
          };
          break;
        }
        case DataType.List:
        case DataType.Boolean: {
          const options = this.getSelectionOptions(x);

          gridColumn = {
            ...gridColumn,
            display: (data) => {
              return this.canEditInlineCustomData(data, x.id)
                ? {
                    type: this.markAsMandatory(data, x.id)
                      ? MandatoryTextComponent
                      : SelectDisplayComponent,
                    config: { options },
                  }
                : { type: this.customDataNotApplicableTemplate };
            },
            edit: {
              type: SelectEditComponent,
              config: {
                options,
                includeNoneOption: true,
                noneOptionName: getStaticMessage(StaticMessage.NoneOptionName),
              },
              allowEditing: (data) => this.canEditInlineCustomData(data, x.id),
            },
            tooltip: {
              ...gridColumn.tooltip,
              message: selectTooltipMessage(options),
            },
          };

          break;
        }
        case DataType.Percentage: {
          gridColumn = {
            ...gridColumn,
            header: {
              ...header,
              alignment: GridAlignment.Right,
            },
            columnAlignment: GridAlignment.Right,
            display: (data) => {
              return this.canEditInlineCustomData(data, x.id)
                ? {
                    type: this.markAsMandatory(data, x.id)
                      ? MandatoryTextComponent
                      : TextDisplayComponent,
                    config: {
                      options: {
                        allowPercentage: true,
                      },
                    },
                  }
                : { type: this.customDataNotApplicableTemplate };
            },
            edit: {
              type: NumericEditComponent,
              config: {
                options: {
                  step: 0.01,
                  decimalPrecision: 2,
                  allowPercentage: true,
                  allowSign: false,
                },
              },
              allowEditing: (data) => this.canEditInlineCustomData(data, x.id),
            },
          };
          break;
        }
        case DataType.Date: {
          gridColumn = {
            ...gridColumn,
            header: {
              ...header,
              alignment: GridAlignment.Right,
            },
            columnAlignment: GridAlignment.Right,
            minWidth: 100,
            display: (data) => {
              return this.canEditInlineCustomData(data, x.id)
                ? {
                    type: this.markAsMandatory(data, x.id)
                      ? MandatoryDateComponent
                      : DateDisplayComponent,
                  }
                : { type: this.customDataNotApplicableTemplate };
            },
            edit: {
              type: DateEditComponent,
              config: {
                allowClearOption: true,
                clearButtonLabel: getStaticMessage(StaticMessage.ClearButton),
                timeOptions: {
                  withoutTimeZone: true,
                },
              },
              delegateEndEdit: true,
              allowEditing: (data) => this.canEditInlineCustomData(data, x.id),
            },
          };
          break;
        }
        case DataType.Datetime: {
          gridColumn = {
            ...gridColumn,
            header: {
              ...header,
              alignment: GridAlignment.Right,
            },
            columnAlignment: GridAlignment.Right,
            minWidth: 160,
            display: (data) => {
              return this.canEditInlineCustomData(data, x.id)
                ? {
                    type: this.markAsMandatory(data, x.id)
                      ? MandatoryDateComponent
                      : DateDisplayComponent,
                    config: {
                      format:
                        GridConstants.DefaultValues.dateDisplayFormatWithTime,
                    },
                  }
                : { type: this.customDataNotApplicableTemplate };
            },
            edit: {
              type: DateEditComponent,
              config: {
                saveButtonLabel: getStaticMessage(StaticMessage.SaveButton),
                clearButtonLabel: getStaticMessage(StaticMessage.ClearButton),
                timeOptions: {
                  allowTimePicker: true,
                  timePickerComponent: this.timeComponent,
                  withoutTimeZone: true,
                },
                format: GridConstants.DefaultValues.dateDisplayFormatWithTime,
              },
              delegateEndEdit: true,
              allowEditing: (data) => this.canEditInlineCustomData(data, x.id),
            },
          };
          break;
        }
      }

      columns.push(gridColumn);
    });

    return columns;
  }

  get selectedSavedActivities(): DprActivityGridData[] {
    return this.gridApi$.value
      ?.getSelection()
      .filter((value) => typeof value.id === 'number');
  }

  get lastRowData(): DprActivityGridData {
    const sortDirection = this.selectedSortOption.definition[0].direction;

    if (sortDirection === GridSortDirection.Ascending) {
      return this.gridData?.[this.gridData.length - 1];
    } else {
      return this.gridData?.[0];
    }
  }

  //#endregion

  //#region Lifecycle Hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isHandset?.currentValue === false && this.dialogModeRef) {
      this.dialogModeRef.close();
    }

    if (changes.canEdit || changes.isAppOnline || changes.isHandset) {
      this.initializeGrid();
    }

    if (changes.selectedSubproject || changes.activityStream) {
      this.activityMobilisation =
        this.dprActivitiesGridService.getActivityMobilisation(
          this.activityStream,
          this.projectSetup.activityMobilisationList
        );
    }

    if (changes.selectedSubproject) {
      this.initializeGrid();
    }

    if (changes.isAppOnline && !changes.isAppOnline.currentValue) {
      this.copyActivityDialogRef?.close();
    }
  }

  //#endregion

  /**
   * @description Initializes the grid by computing the grid options and building up the grid columns
   * according to the selected activity stream
   */
  initializeGrid(): void {
    this.gridColumns$.next(this.getGridColumns());
    this.gridOptions$.next(this.getGridOptions());
  }

  checkRouteParameters(): void {
    setTimeout(() => {
      combineLatest([
        this.gridColumns$,
        this.gridApi$.asObservable(),
        this.activatedRoute.queryParams,
      ])
        .pipe(
          untilDestroyed(this),
          filter(([gridColumns, gridApi]) => !!gridColumns && !!gridApi),
          take(1),
          tap(([_, gridApi, params]) => {
            if (params.modal === 'import-data') {
              if (!this.importActivitiesDialogRef) {
                this.openDprImportActivitiesDialog();
              }
              return;
            }

            if (isNaN(params.selectedTab)) {
              this.navigateToBaseRoute();
              return;
            }

            this.openModalFromRoute(gridApi, {
              // this is needed so that he user doesn't receive an empty modal in case they change the selectedTab parameter
              // to a tab that doesn't exists
              selectedTab: Object.values(DprActivityData).includes(
                +params.selectedTab
              )
                ? +params.selectedTab
                : DprActivityData.CustomData,
            });
          })
        )
        .subscribe();
    });
  }

  onSortOptionChanged(sortOption: any): void {
    this.selectedSortOption = sortOption;

    this.api.setSortingDefinition(sortOption.definition);
  }

  onCopyActivity(data: DprActivityGridData): void {
    const copyOptions = this.dprActivityCopyService.getOptions(data);

    if (!copyOptions.length) {
      return this.copyActivity(data);
    }

    this.dialog
      .open(DprDetailsCopyActionModalComponent, {
        ...Constants.DialogOptions,
        disableClose: false,
        data: {
          data,
          title: this.getActivityTitle(data),
          options: copyOptions,
        },
        panelClass: 'dialogCustom--maxHeight',
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter((result) => !!result)
      )
      .subscribe((result: DprDetailCopyOption[]) => {
        this.copyActivity(data, result);
      });
  }

  onCopySelectedActivities(): void {
    this.copyActivityDialogRef = this.dialog.open(
      DprCopyActivityDialogEditComponent,
      {
        ...Constants.DialogOptions,
        disableClose: false,
        data: {
          title: $localize`:@@dprDetails.dprActivitiesGrid.dprCopyActivityDialog.titleDprCopyActivitiesDialog:${this.activityStream.value}:activityStreamName:: Specify copy destination and what details to copy`,
          dprDetails: this.dprDetails,
          subproject: this.selectedSubproject,
          selectedSavedActivities: this.selectedSavedActivities,
          canEdit:
            this.selectedSubproject.dprScreen === Rights.Edit &&
            !this.selectedSubproject.isLocked,
          availableDates: this.availableDates,
          defaultActivityStream: this.activityStream,
        },
      }
    );

    this.copyActivityDialogRef
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter((result: DprActivityCopyOption) => !!result),
        tap((result: DprActivityCopyOption) =>
          this.checkCanCopySelectedActivities(result)
        )
      )
      .subscribe();
  }

  isRowEmpty(data: DprActivityGridData): boolean {
    return (
      data.activityGroupId == null &&
      data.activityId == null &&
      data.locationId == null &&
      String.isNullOrWhiteSpace(data.start) &&
      String.isNullOrWhiteSpace(data.finish) &&
      !data.dprSubActivities?.length &&
      !data.dprActivityCustomDataList?.length &&
      !data.dprActivityAttachments?.length
    );
  }

  validateChanges(changes: GridSync<DprActivityGridData>): boolean {
    const entities = [
      ...(changes?.addedList ?? []),
      ...(changes?.updatedList ?? []),
    ];
    const errorMessages = [];

    for (const entity of entities) {
      if (!entity.activityGroupId) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.activityGroupFieldName
          )
        );
      }

      if (!entity.activityId) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.gridEntityName
          )
        );
      }

      if (!entity.locationId) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.locationFieldName
          )
        );
      }

      if (!entity.start) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.startFieldName
          )
        );
      }

      if (!entity.finish) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.finishFieldName
          )
        );
      }

      if (
        entity.start &&
        entity.finish &&
        compareTime(
          convertTimeToDate(entity.start),
          convertTimeToDate(entity.finish)
        ) > 0
      ) {
        errorMessages.push(
          $localize`:@@dprDetails.dprActivitiesGrid.errStartMustBeBeforeFinish:- Start must be before Finish.`
        );
      }

      if (errorMessages.length) {
        const activityType = this.projectSetup?.activityStreams?.find(
          (x) => x.parallelActivityType === (entity.parallelActivityType ?? 0)
        )?.value;

        this.dialog.open(ConfirmDialogComponent, {
          ...Constants.DialogOptions,
          data: {
            extra: activityType,
            title: getStaticMessage(StaticMessage.ValidationErrorTitle),
            message: errorMessages.join('<br>'),
            buttonLabel: getStaticMessage(StaticMessage.OkButton),
            hideCancelButton: true,
          },
        });

        return false;
      }
    }

    return true;
  }

  openAddNewEntry(): void {
    this.openEntryInDialogMode();
  }

  openEditEntry(data: DprActivityGridData): void {
    this.openEntryInDialogMode(data);
  }

  onImportActivities(): void {
    this.navigateToBaseRouteWithParams({
      modal: 'import-data',
    });

    this.openDprImportActivitiesDialog();
  }

  openDprImportActivitiesDialog(): void {
    this.importActivitiesDialogRef = this.dialog.open(
      DprImportActivitiesDialogComponent,
      {
        ...Constants.DialogOptions,
        panelClass: 'dialogCustom--fullScreen',
        height: '100vh',
        data: {
          title: $localize`:@@dprDetails.dprActivitiesGrid.dprImportActivitiesDialog.titleDprImportActivitiesDialog:${this.activityStream.value}:activityStreamName:: Import Data`,
          worksheetName: `DPR_${this.toUtcDateTimePipe.transform(
            this.dprDetails.dprDateTime,
            'dd-MM-yyyy'
          )}_Activities_${this.activityStream.value.replace(/\s/g, '_')}`,
          parallelActivityType: this.activityStream.parallelActivityType,
          inlineCustomData: this.inlineCustomData,
          activityMobilisationList: this.activityMobilisation,
          getActivityGroups: () =>
            this.dprActivitiesGridService.getActivityGroupOptions(
              this.activityStream.id,
              this.activityGroups
            ),
          getActivities: (activityGroupId: number) =>
            this.dprActivitiesGridService.getActivityOptions(
              activityGroupId,
              this.activities
            ),
          getLocations: (activityId: number) =>
            this.dprActivitiesGridService.getLocationOptions(
              activityId,
              this.locations,
              this.activities
            ),
          getSelectionOptions: (customData: CustomData) =>
            this.getSelectionOptions(customData),
        },
      }
    );

    this.importActivitiesDialogRef
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter((result: DprActivityGridData[]) => !!result),
        tap((result: DprActivityGridData[]) => {
          result
            .filter((entry) => !!entry)
            .forEach((entry) => {
              entry.dprDetailId = this.dprDetails.id;
              entry.status = this.computeRowStatus(entry);
              this.setRowInlineCustomDataValues(entry);
              this.api?.addRows(entry);
            });
        }),
        finalize(() => {
          // TODO: LAUTEC-6695 -- Remove this emit after the refactorization of the Deactivation Guarded Component
          this.reactivateParentContainer.emit();
          this.navigateToBaseRoute();
        })
      )
      .subscribe();
  }

  protected getGridOptions(): GridOptions<DprActivityGridData> {
    return {
      labeling: {
        ...GridDefaultLabels,
        hideLabel: this.isHandset ? true : false,
      },
      addConfig: {
        allowAdding: this.canEdit && this.activityStream?.isActive,
        buttonPosition: 'floating',
        value: !this.isHandset ? this.addRow.bind(this) : null,
        onAdded: this.computeStatusForAllRows.bind(this),
        keepAutogeneratedIds: true,
        autoStartEdit: false,
        mode: this.isHandset ? 'dialog' : 'inline',
        disableAddShortcut: this.isHandset,
        buttonTemplate: this.isHandset ? this.addDialogButtonTemplate : null,
      },
      editConfig: {
        allowEditing:
          this.canEdit && this.activityStream?.isActive && !this.isHandset,
        mode: this.isHandset ? 'dialog' : 'inline',
        onRowClick: this.isHandset ? this.openEditEntry.bind(this) : null,
      },
      deleteConfig: {
        allowDeleting: this.canEdit && this.activityStream?.isActive,
        onDeleted: this.computeStatusForAllRows.bind(this),
      },
      copyConfig: {
        allowCopying:
          this.isAppOnline &&
          this.selectedSubproject.dprScreen === Rights.Edit &&
          !this.selectedSubproject.isLocked,
        onCopySelectedRows: this.onCopySelectedActivities.bind(this),
        copyRowsTemplate: this.copyActivitiesTemplate,
      },
      saveConfig: {
        isExternalSave: true,
      },
      importConfig: {
        allowImporting:
          this.selectedSubproject.dprScreen === Rights.Edit &&
          !this.selectedSubproject.isLocked &&
          this.activityStream?.isActive &&
          !this.dprDetails?.lockedDate,
        importTemplate: this.importActivitiesTemplate,
        onImport: this.onImportActivities.bind(this),
      },
      rowConfig: {
        allowDeleting: (data: DprActivityGridData) =>
          data.canBeDeleted == null || data.canBeDeleted,
        allowSelection: (data: DprActivityGridData) =>
          data.canBeDeleted == null || data.canBeDeleted,
        isEmpty: this.isRowEmpty.bind(this),
      },
      sortConfig: {
        mode: GridSortMode.Selection,
        definition: this.selectedSortOption.definition,
        selectionTemplate: this.sortTemplate,
      },
      selectionConfig: {
        allowSelection:
          this.selectedSubproject.dprScreen === Rights.Edit &&
          !this.selectedSubproject.isLocked &&
          this.activityStream?.isActive &&
          (!this.dprDetails?.lockedDate || this.isAppOnline),
      },
      events: {
        onGridRegistered: (gridApi: GridApi<DprActivityGridData>) => {
          this.gridRegistered.emit(gridApi);
          this.gridApi$.next(gridApi);
        },
        onDataReady: () => {
          this.setInlineCustomDataValuesForAllRows();

          this.computeStatusForAllRows();
          this.checkRouteParameters();
          this.cdr.markForCheck();
        },
      },
    };
  }

  saveDialogDataAndClose(): void {
    const result = this.dialogModeRef?.componentInstance?.autoSaveCloseDialog();
    this.reactivateParentContainer.emit();
    this.handleDialogResult(result, false);
  }

  closeImportActivitiesDialog(): void {
    this.importActivitiesDialogRef?.close();
  }

  protected getGridColumns(): GridColumn<DprActivityGridData>[] {
    return [
      {
        columnDef: 'status',
        header: {
          name: $localize`:@@dprDetails.dprActivitiesGrid.colStatus:Status`,
          alignment: GridAlignment.Center,
        },
        columnAlignment: GridAlignment.Center,
        allowSorting: false,
        display: {
          type: this.statusTemplate,
        },
        width: 65,
        tabIndex: -1,
        disableFocusedBackground: true,
      },
      {
        columnDef: 'activityGroupId',
        header: {
          name: this.activityGroupFieldName,
        },
        allowSorting: false,
        display: {
          type: SelectDisplayComponent,
          config: {
            options: this.getSelectActivityGroupOptions.bind(this),
          },
        },
        edit: {
          type: SelectEditComponent,
          config: {
            options: this.getActivityGroupOptions.bind(this),
          },
          allowEditing: () => !this.isHandset,
        },
        onCellEdited: this.activityGroupChanged.bind(this),
        tooltip: {
          message: selectTooltipMessage(
            this.getActivityGroupOptions.bind(this)
          ),
          disabled: this.isHandset,
        },
        minWidth: 200,
      },
      {
        columnDef: 'activityId',
        header: {
          name: this.gridEntityName,
        },
        allowSorting: false,
        display: {
          type: SelectDisplayComponent,
          config: {
            options: this.getSelectActivityOptions.bind(this),
          },
        },
        edit: {
          type: SelectEditComponent,
          config: {
            options: this.getActivityOptions.bind(this),
          },
          allowEditing: (data) => !this.isHandset && !!data.activityGroupId,
        },
        onCellEdited: this.activityChanged.bind(this),
        tooltip: {
          message: selectTooltipMessage(this.getActivityOptions.bind(this)),
          disabled: this.isHandset,
        },
        minWidth: 200,
        recomputeRowOnChange: true,
      },
      {
        columnDef: 'locationId',
        header: {
          name: this.locationFieldName,
        },
        allowSorting: false,
        display: {
          type: SelectDisplayComponent,
          config: {
            options: this.getSelectLocationOptions.bind(this),
          },
        },
        edit: {
          type: SelectEditComponent,
          config: {
            options: this.getLocationOptions.bind(this),
          },
          allowEditing: (data) => !this.isHandset && !!data.activityId,
        },
        onCellEdited: this.locationChanged.bind(this),
        tooltip: {
          message: selectTooltipMessage(this.getLocationOptions.bind(this)),
          disabled: this.isHandset,
        },
        minWidth: 200,
      },
      {
        columnDef: 'start',
        header: {
          name: this.startFieldName,
          alignment: GridAlignment.Center,
        },
        columnAlignment: GridAlignment.Center,
        allowSorting: false,
        display: { type: TimeDisplayComponent },
        edit: { type: TimeEditComponent },
        onCellEdited: (data) => {
          if (this.findActivityById(data.activityId)?.isMilestone) {
            this.api.editCell(data, 'finish', data.start);
          }

          this.computeStatusForAllRows();
        },
        tooltip: {
          trigger: this.subprojectConfiguration
            .permanentlyDisplayStartEndDefinitions
            ? 'manual'
            : 'mouseenter',
          hideOnClick:
            this.subprojectConfiguration.permanentlyDisplayStartEndDefinitions,
          onlyTextOverflow: false,
          property: 'activityId',
          message: (context) => {
            if (context.data.activityId === null) {
              return null;
            }

            const activity = this.findActivityById(context.data.activityId);

            return activity?.startDefinition;
          },
          disabled: (context) =>
            this.isHandset ||
            context.data.activityId == null ||
            String.isNullOrWhiteSpace(
              this.findActivityById(context.data.activityId)?.startDefinition
            ),
        },
        width: 90,
      },
      {
        columnDef: 'finish',
        header: {
          name: this.finishFieldName,
          alignment: GridAlignment.Center,
        },
        columnAlignment: GridAlignment.Center,
        allowSorting: false,
        display: { type: TimeDisplayComponent },
        edit: {
          type: TimeEditComponent,
          allowEditing: (data) =>
            !this.findActivityById(data.activityId)?.isMilestone,
        },
        onCellEdited: () => {
          this.computeStatusForAllRows();
        },
        tooltip: {
          trigger: this.subprojectConfiguration
            .permanentlyDisplayStartEndDefinitions
            ? 'manual'
            : 'mouseenter',
          hideOnClick:
            this.subprojectConfiguration.permanentlyDisplayStartEndDefinitions,
          onlyTextOverflow: false,
          property: 'activityId',
          message: (context) => {
            if (context.data.activityId === null) {
              return null;
            }

            const activity = this.findActivityById(context.data.activityId);

            return activity?.endDefinition;
          },
          disabled: (context) =>
            this.isHandset ||
            context.data.activityId == null ||
            String.isNullOrWhiteSpace(
              this.findActivityById(context.data.activityId)?.endDefinition
            ),
        },
        width: 90,
      },
      {
        columnDef: 'duration',
        header: {
          name: getStaticMessage(StaticMessage.DurationColumn),
          alignment: GridAlignment.Center,
        },
        columnAlignment: GridAlignment.Center,
        allowSorting: false,
        display: { type: this.durationTemplate },
        tabIndex: -1,
        disableFocusedBackground: true,
        width: 100,
      },
      ...this.inlineCustomDataColumns,
      {
        ...GridColumns.commentFn<DprActivityGridData>(
          this.getActivityTitle.bind(this),
          null,
          () => {
            this.reactivateParentContainer.emit();
          }
        ),
        ...(this.isHandset && { edit: undefined }),
      },
      {
        columnDef: 'info',
        header: {
          name: $localize`:@@dprDetails.dprActivitiesGrid.colInfo:Info`,
        },
        allowSorting: false,
        allowEditing: this.canEdit && this.activityStream?.isActive,
        display: { type: this.infoTemplate },
        edit: (data: DprActivityGridData) => ({
          type: DprEditActivityDialogEditComponent,
          mode: 'dialog',
          delegateStartEdit: true,
          config: {
            ...Constants.DialogOptions,
            additionalData: {
              activityMobilisation: this.activityMobilisation,
              selectedSubproject: this.selectedSubproject,
              isMilestoneActivity: this.activities.find(
                (x) => x.id === data.activityId
              )?.isMilestone,
              title: this.getActivityTitle(data),
              dprLockedDate: this.dprDetails.lockedDate,
            },
            beforeOpenAdaptor: (activityData, additionalData) => {
              this.navigateToBaseRouteWithParams({
                modal: 'info',
                entity: activityData.id,
                selectedTab: additionalData.selectedTab,
              });
            },
            afterCloseAdaptor: () => {
              this.reactivateParentContainer.emit();
              this.navigateToBaseRoute();
            },
            panelClass: 'dialogCustom--largeSize',
          },
        }),
        onCellEdited: () => {
          this.computeStatusForAllRows();
          this.setInlineCustomDataValuesForAllRows();
        },
        width: 150,
        tabIndex: -1,
        disableFocusedBackground: true,
      },
      {
        ...GridColumns.actions,
        display: { type: this.actionsTemplate },
        allowEditing: this.canEdit && this.activityStream?.isActive,
        edit: (data: DprActivityGridData) => ({
          type: DprEditActivityDialogEditComponent,
          mode: 'dialog',
          delegateStartEdit: true,
          config: {
            ...Constants.DialogOptions,
            additionalData: {
              activityMobilisation: this.activityMobilisation,
              selectedSubproject: this.selectedSubproject,
              isMilestoneActivity: this.activities.find(
                (x) => x.id === data.activityId
              )?.isMilestone,
              title: this.getActivityTitle(data),
              dprLockedDate: this.dprDetails.lockedDate,
            },
            beforeOpenAdaptor: (activityData, additionalData) => {
              this.navigateToBaseRouteWithParams({
                modal: 'info',
                entity: activityData.id,
                selectedTab: additionalData.selectedTab,
              });
            },
            afterCloseAdaptor: () => {
              this.reactivateParentContainer.emit();
              this.navigateToBaseRoute();
            },
            panelClass: 'dialogCustom--largeSize',
          },
        }),
        onCellEdited: () => {
          this.computeStatusForAllRows();
          this.setInlineCustomDataValuesForAllRows();
          // We need this to re-render the grid in case mandatory CD have been filled
          // in the CD modal so that the markAsMandatory method is re-evaluated for inline CD
          // otherwise the values filled in the dialog are not visible for inline mandatory CD cells
          this.api.repaintRows();
        },
      },
    ];
  }

  //#region Private methods

  /**
   * @description Checks if the inline custom data can be edited with regards to the following conditions
   * 1. Activity mobilisation is active
   * 2. If activity mobilisation is not active that allow editing if the custom data has a value set
   */
  private canEditInlineCustomData(
    data: DprActivityGridData,
    customDataId: number
  ): boolean {
    const mobilisation = this.activityMobilisation.find(
      (x) => x.activityId === data.activityId && x.customDataId === customDataId
    );

    // custom data is not assigned to selected activity activity
    if (!mobilisation) {
      return false;
    }

    // activity mobilisation is active, it is save to be used
    if (mobilisation.isActive) {
      return true;
    }

    const customData = this.inlineCustomData.find((x) => x.id === customDataId);
    const dprActivityCustomData = data.dprActivityCustomDataList.find(
      (x) => x.customDataId === customDataId
    );

    return (
      // allow edit if it is inactivated but has value assigned
      hasValue(dprActivityCustomData?.value, customData.dataTypeId) ||
      // value was cleared, allow edit until data is saved
      this.api?.isPropertyChanged(data, String(customDataId))
    );
  }

  private markAsMandatory(
    data: DprActivityGridData,
    customDataID: number
  ): boolean {
    return (
      this.activityMobilisation.find(
        (x) =>
          x.activityId === data.activityId && x.customDataId === customDataID
      )?.isMandatory &&
      (!data.dprActivityCustomDataList.find(
        (x) => x.customDataId === customDataID
      ) ||
        String.isNullOrWhiteSpace(data[customDataID]?.toString())) &&
      !this.dprDetails.lockedDate
    );
  }

  private inlineCustomDataChanged(
    data: DprActivityGridData,
    customDataId: number
  ): void {
    const value = data[customDataId] ?? null;

    let dprActivityCustomData = data.dprActivityCustomDataList.find(
      (x) => x.customDataId === customDataId
    );

    if (dprActivityCustomData) {
      dprActivityCustomData.value = value;
    } else {
      dprActivityCustomData = {
        id: uuid(),
        dprActivityId: data.id as number,
        customDataId,
        value,
        comment: null,
      };

      data.dprActivityCustomDataList.push(dprActivityCustomData);
    }

    data.syncDprActivityCustomDataList = this.mergeService.addDataToChanges(
      data.syncDprActivityCustomDataList,
      dprActivityCustomData,
      isNaN(+dprActivityCustomData.id)
    );

    // change reference in order to update the ui info flag
    data.dprActivityCustomDataList = [...data.dprActivityCustomDataList];
  }

  /**
   * @description Sets the inline custom data values for all of the grid rows
   */
  private setInlineCustomDataValuesForAllRows(): void {
    this.gridData.forEach((data) => {
      this.setRowInlineCustomDataValues(data);
    });
  }

  /**
   * @description Sets the inline custom data values for the given row
   */
  private setRowInlineCustomDataValues(data: DprActivityGridData): void {
    if (data == null) {
      return;
    }

    this.inlineCustomData.forEach((customData) => {
      const dprActivityCustomData = data.dprActivityCustomDataList.find(
        (y) => y.customDataId === customData.id
      );

      data[customData.id] = dprActivityCustomData?.value?.toString() ?? null;
    });
  }

  private activityGroupChanged(data: DprActivityGridData): void {
    let activityId: number = null;

    // auto-select the activity if there is only one possible option
    if (data.activityGroupId) {
      const activities = this.getActivityOptions(data);
      if (activities.length === 1) {
        activityId = +activities[0].id;
      }
    }

    if (data.activityId !== activityId) {
      this.api.editCell(data, 'activityId', activityId);
    }

    data.status = this.computeRowStatus(data);
  }

  private activityChanged(data: DprActivityGridData): void {
    let selectedLocationId = data.locationId;

    if (data.activityId) {
      const locations = this.getLocationOptions(data);

      // check if the selected location is allowed for selected activity
      if (!locations.some((x) => x.id === data.locationId)) {
        selectedLocationId = null;
      }

      // auto-select the activity if there is only one possible option
      if (locations.length === 1) {
        selectedLocationId = +locations[0].id as number;
      }
    }

    if (data.locationId !== selectedLocationId) {
      this.api.editCell(data, 'locationId', selectedLocationId);
    }

    if (this.findActivityById(data.activityId)?.isMilestone) {
      this.api.editCell(data, 'finish', data.start);
    }

    data.status = this.computeRowStatus(data);

    this.filterCustomDataListOnActivityChanged(data);

    this.refreshInlineCustomDataDisplay();
  }

  private locationChanged(data: DprActivityGridData): void {
    data.status = this.computeRowStatus(data);
  }

  private getSelectActivityGroupOptions(
    data?: DprActivityGridData
  ): SelectionItem[] {
    const activityGroupsOptions = this.getActivityGroupOptions(data);

    if (
      data.activityGroupId &&
      !activityGroupsOptions.find((x) => x.id === data.activityGroupId)
    ) {
      activityGroupsOptions.push(
        this.activityGroups.find((x) => x.id === data.activityGroupId)
      );
    }

    return activityGroupsOptions as SelectionItem[];
  }

  private getActivityGroupOptions(data?: DprActivityGridData): SelectionItem[] {
    const activityGroups =
      this.dprActivitiesGridService.getActivityGroupOptions(
        this.activityStream.id,
        this.activityGroups,
        data?.activityGroupId
      );

    return activityGroups as SelectionItem[];
  }

  private getSelectActivityOptions(data: DprActivityGridData): SelectionItem[] {
    const activityOptions = this.getActivityOptions(data);

    if (
      data.activityId &&
      !activityOptions.find((x) => x.id === data.activityId)
    ) {
      activityOptions.push(
        this.activities.find((x) => x.id === data.activityId)
      );
    }

    return activityOptions as SelectionItem[];
  }

  private getActivityOptions(data: DprActivityGridData): SelectionItem[] {
    if (!data.activityGroupId) {
      return [];
    }

    const activityGroups = this.dprActivitiesGridService.getActivityOptions(
      data.activityGroupId,
      this.activities,
      data.activityId
    );

    return activityGroups as SelectionItem[];
  }

  private getSelectLocationOptions(data: DprActivityGridData): SelectionItem[] {
    const locationOptions = this.getLocationOptions(data);
    if (
      data.locationId &&
      !locationOptions.find((x) => x.id === data.locationId)
    ) {
      locationOptions.push(
        this.locations.find((x) => x.id === data.locationId)
      );
    }

    return locationOptions;
  }

  private getLocationOptions(data: DprActivityGridData): SelectionItem[] {
    if (!data.activityId) {
      return [];
    }

    const locations = this.dprActivitiesGridService.getLocationOptions(
      data.activityId,
      this.locations,
      this.activities,
      data.locationId
    );

    return locations as SelectionItem[];
  }

  private computeRowStatus(data: DprActivityGridData): WarningStatusType {
    return this.dprActivityStatusService.getStatus(
      data,
      this.gridData,
      this.projectSetup,
      this.selectedSortOption.definition[0].direction ===
        GridSortDirection.Descending
    );
  }

  private computeStatusForAllRows(): void {
    this.gridData.forEach((data) => {
      data.status = this.computeRowStatus(data);
    });
  }

  private addRow(): Partial<DprActivityGridData> {
    const lastRow = this.lastRowData;

    if (this.userPreferences.enableFinishTimeAutoPopulationForActivities) {
      this.autoPopulateFinishTime(lastRow);
    }

    const result = this.dprActivityCreateService.createActivity(
      this.dprDetails.id,
      lastRow,
      this.activityStream,
      this.projectSetup,
      this.userPreferences
    );

    return result;
  }

  private autoPopulateFinishTime(lastRow: DprActivityGridData): void {
    // if there is no start time for the last row or the finish is filled -> don't update finish time
    if (!lastRow?.start || lastRow?.finish) {
      return;
    }

    //get the current time as string
    const currentTime = getCurrentTimeAsString();

    // if current time is higher than last row start time -> skip
    if (
      compareTime(
        convertTimeToDate(lastRow.start),
        convertTimeToDate(currentTime)
      ) > 0
    ) {
      return;
    }

    this.gridApi$.value.editCell(lastRow, 'finish', currentTime);
  }

  private copyActivity(
    source: DprActivityGridData,
    options?: DprDetailCopyOption[]
  ): void {
    const data = this.dprActivityCopyService.copy(source, options);

    this.setRowInlineCustomDataValues(data);

    data.status = this.computeRowStatus(data);

    this.api?.addRows(data);
  }

  private getActivityTitle(data: DprActivityGridData): string {
    const activityGroupName =
      this.activityGroups.find((x) => x.id === data.activityGroupId)?.name ??
      this.activityGroupFieldName;

    const activityName =
      this.findActivityById(data.activityId)?.name ?? this.gridEntityName;

    return `${activityGroupName}, ${activityName}`;
  }

  private findActivityById(activityId: number): Activity {
    return this.projectSetup.activityList.find((x) => x.id === activityId);
  }

  private filterCustomDataListOnActivityChanged(
    data: DprActivityGridData
  ): void {
    if (data.activityId === null) {
      return;
    }

    const customDataToDelete = data.dprActivityCustomDataList.filter(
      (x) => !this.getCorrespondingActivityMobilisation(data, x)
    );
    data.dprActivityCustomDataList = data.dprActivityCustomDataList.filter(
      (x) => this.getCorrespondingActivityMobilisation(data, x)
    );

    // delete no longer valid inline custom data
    if (customDataToDelete.length) {
      customDataToDelete.forEach((cd) => delete data[cd.customDataId]);
    }

    if (data.syncDprActivityCustomDataList) {
      data.syncDprActivityCustomDataList = {
        addedList: this.getSharedCustomData(data, 'addedList'),
        deletedIds: [],
        updatedList: this.getSharedCustomData(data, 'updatedList'),
      };
    } else {
      data.syncDprActivityCustomDataList = {
        addedList: [],
        deletedIds: customDataToDelete.map((x) => x.id),
        updatedList: [],
      };
    }
  }

  private getSharedCustomData(
    data: DprActivityGridData,
    property: string
  ): DprActivityCustomDataGridData[] {
    const dprActivityGridDataCopy = cloneDeep(data);
    return dprActivityGridDataCopy.syncDprActivityCustomDataList[
      property
    ].filter((x: DprActivityCustomDataGridData) =>
      this.getCorrespondingActivityMobilisation(data, x)
    );
  }

  private refreshInlineCustomDataDisplay(): void {
    this.inlineCustomData.forEach((x) => {
      const inlineCdColumn = this.gridColumns$.value.find(
        (y) => y.columnDef === `${x.id}`
      );
      if (inlineCdColumn) {
        inlineCdColumn.tabIndex = (rowData) =>
          this.canEditInlineCustomData(rowData, x.id) ? 0 : -1;
        inlineCdColumn.disableFocusedBackground = (rowData) =>
          !this.canEditInlineCustomData(rowData, x.id);
      }
    });
  }

  private getSelectionOptions(customData: CustomData): SelectionItem[] {
    switch (customData.dataTypeId) {
      case DataType.List:
        return this.projectSetup.customDataValueList
          .filter(
            (customDataValue) => customDataValue.customDataId === customData.id
          )
          .map<SelectionItem>((customDataValue) => ({
            // this is needed because the customDataId property is assigned dynamically to the object
            // and the id will have a string value in this case
            id: customDataValue.id.toString(),
            name: customDataValue.value,
          }));
      case DataType.Boolean:
        return buildBooleanValues(
          customData.booleanTerminologyId
        ).map<SelectionItem>((booleanValue) => ({
          id: booleanValue.id.toString(),
          name: booleanValue.value,
        }));
      default:
        return [];
    }
  }

  private openEntryInDialogMode(data?: DprActivityGridData): void {
    this.dialogModeRef = this.dialog.open(DprActivitiesDialogModeComponent, {
      ...Constants.DialogOptions,
      panelClass: 'dialogCustom--mobileFullScreen',
      position: { top: '0' },
      autoFocus: 'dialog',
      delayFocusTrap: true,
      data: {
        gridRowData: data,
        activityGroupOptions:
          this.dprActivitiesGridService.getActivityGroupOptions(
            this.activityStream.id,
            this.projectSetup.activityGroupList
          ),
        activityStream: this.activityStream,
        customDataList: this.projectSetup.customDataList,
        inlineCustomData: this.inlineCustomData,
        activityMobilisations: this.activityMobilisation,
        enableFinishTimeAutoPopulationForActivities:
          this.userPreferences.enableFinishTimeAutoPopulationForActivities,
        canEdit: this.activityStream?.isActive && this.canEdit,
        dprDetailId: this.dprDetails.id,
        getActivities: (activityGroupId: number) =>
          this.dprActivitiesGridService.getActivityOptions(
            activityGroupId,
            this.activities
          ),
        getLocations: (activityId: number) =>
          this.dprActivitiesGridService.getLocationOptions(
            activityId,
            this.locations,
            this.activities
          ),
        createActivity: () =>
          this.dprActivityCreateService.createActivity(
            this.dprDetails.id,
            this.lastRowData,
            this.activityStream,
            this.projectSetup,
            this.userPreferences
          ),
        isMilestone: (activityId: number) =>
          this.findActivityById(activityId)?.isMilestone,
        getSelectionOptions: (customData: CustomData) =>
          this.getSelectionOptions(customData),
      },
    });

    this.dialogModeRef
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(
          (result: { data: DprActivityGridData; hasChanges: boolean }) =>
            !!result?.data
        ),
        tap((result) => {
          this.reactivateParentContainer.emit();
          this.handleDialogResult(result);
        })
      )
      .subscribe();
  }

  private handleDialogResult(
    result: {
      data: DprActivityGridData;
      hasChanges: boolean;
    },
    displayToast: boolean = true
  ): void {
    const existingData = this.gridData.find((x) => x.id === result.data.id);
    let message: string;

    if (!result.hasChanges) {
      return;
    }

    if (existingData) {
      // Update existing row
      const hasChanges = this.api.updateRow(result.data);

      if (!hasChanges) {
        return;
      }

      if (
        !this.syncService.isSyncModelEmpty(
          result.data.syncDprActivityCustomDataList
        )
      ) {
        this.api.editCell(
          result.data,
          'info',
          result.data.dprActivityCustomDataList
        );
      }

      message = $localize`:@@dprDetails.dprActivitiesGrid.txtActivityEdited:Activity successfully edited`;
    } else {
      // Add new row
      this.api.addRows(result.data);
      setTimeout(() => this.api.scrollToLastRow());

      message = $localize`:@@dprDetails.dprActivitiesGrid.txtActivityAdded:Activity successfully added`;
    }

    this.setInlineCustomDataValuesForAllRows();
    this.computeStatusForAllRows();

    if (displayToast) {
      this.toastNotificationService.success(message, false);
    }
  }

  private checkCanCopySelectedActivities(options: DprActivityCopyOption): void {
    if (this.api.isDirty()) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        ...Constants.DialogOptions,
        data: {
          title: getStaticMessage(StaticMessage.UnsavedChangesTitle),
          message: getStaticMessage(
            StaticMessage.UnsavedChangesText,
            StaticMessage.CopyText,
            StaticMessage.CopyOperationText
          ),
          buttonLabel: getStaticMessage(StaticMessage.ContinueButton),
        },
      });

      confirmDialog
        .afterClosed()
        .pipe(
          untilDestroyed(this),
          filter((result) => result),
          tap(() => this.copyActivities.emit(options))
        )
        .subscribe();
    } else {
      this.copyActivities.emit(options);
    }
  }

  private getCalculatedCustomDataValue(data: DprActivityGridData): any {
    return this.calculatedCustomDataPipe.transform(
      this.subprojectConfiguration.calculatedCustomDataConfiguration
        .customDataId,
      data.dprActivityCustomDataList,
      data.start,
      data.finish,
      data.activityId
    );
  }

  private getCustomDataFormulaValue(data: DprActivityGridData): any {
    return this.customDataFormulaPipe.transform(
      this.subprojectConfiguration.customDataFormulaConfiguration,
      data.dprActivityCustomDataList,
      data.activityId,
      this.projectSetup.customDataList
    );
  }

  private getCorrespondingActivityMobilisation(
    data: DprActivityGridData,
    customDataGridData: DprActivityCustomDataGridData
  ): ActivityMobilisation {
    return this.projectSetup.activityMobilisationList.find(
      (y) =>
        y.customDataId === customDataGridData.customDataId &&
        y.activityId === data.activityId &&
        (y.parallelActivityType ?? 0) ===
          this.activityStream.parallelActivityType &&
        y.isActive
    );
  }

  //#endregion
}
